import React, { useEffect, useRef, useState } from "react";
import { Avatar } from "primereact/avatar";
import { ProgressSpinner } from "primereact/progressspinner";
import MessageComposer from "./MessageComposer";
import { getMessages, sendMessage } from "../../api/conversationApi";
import { useSocket } from "../../common/contexts/SocketContext";
import { useAuth } from "../../common/contexts/AuthContext";

const formatTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const MessageThread = ({ conversationId, conversation, onMessageSent, readOnly = false }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const containerRef = useRef(null);
  const { socket } = useSocket();
  const { user } = useAuth();

  const otherParticipants = (conversation?.participants || []).filter((p) => !p.isSelf);
  const other = otherParticipants[0]?.user;
  const displayName = conversation?.title || other?.name || "Conversation";

  // Load messages
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getMessages(conversationId, { limit: 50 });
        if (!cancelled && data?.success) {
          setMessages(data.items || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // Join the conversation room and listen for new messages
  useEffect(() => {
    if (!socket || !conversationId) return undefined;
    socket.emit("conversation:join", { conversationId });

    const onNew = (msg) => {
      if (Number(msg.conversation_id) !== Number(conversationId)) return;
      setMessages((prev) => {
        // de-dupe
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    };
    socket.on("message:new", onNew);
    return () => {
      socket.emit("conversation:leave", { conversationId });
      socket.off("message:new", onNew);
    };
  }, [socket, conversationId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (text) => {
    if (!text?.trim()) return;
    try {
      setSending(true);
      const data = await sendMessage(conversationId, { body: text });
      if (data?.success) {
        // The socket will deliver the message back, but to be safe inject locally
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        if (onMessageSent) onMessageSent();
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="message-thread">
      <div className="message-thread-header">
        <Avatar
          label={displayName.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
          shape="circle"
        />
        <div>
          <div className="message-thread-name">{displayName}</div>
          {other?.role?.name && <div className="message-thread-role">{other.role.name}</div>}
        </div>
      </div>

      <div className="message-thread-body" ref={containerRef}>
        {loading ? (
          <div className="message-loading">
            <ProgressSpinner style={{ width: "30px", height: "30px" }} />
          </div>
        ) : messages.length === 0 ? (
          <div className="message-thread-empty">
            <p>No messages yet. Say hello!</p>
          </div>
        ) : (
          messages.map((m) => {
            const isMine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`message-bubble-row ${isMine ? "mine" : "other"}`}>
                <div className="message-bubble">
                  <div className="message-body">{m.body}</div>
                  <div className="message-time">{formatTime(m.createdAt)}</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {readOnly ? (
        <div className="message-thread-readonly">
          <i className="pi pi-eye" /> Oversight — read only. This access is logged.
        </div>
      ) : (
        <MessageComposer onSend={handleSend} disabled={sending} />
      )}
    </div>
  );
};

export default MessageThread;
